<template>
  <component
    :is="elementType"
    :class="buttonClasses"
    :type="computedType"
    :disabled="computedDisabled"
    v-bind="{ ...$attrs, ...linkProps, ...propsWhenDisabled }"
    @click="click"
  >
    <slot></slot>
  </component>
</template>

<script lang="ts">
/* eslint-disable vue/component-api-style */
/**
 * @deprecated Use `<ys-button>` from the latest `@onxmaps/yellowstone-ui` release instead.
 */
export default {
  name: 'XButton',
}
</script>

<script setup lang="ts">
import isFunction from 'lodash/isFunction'
import type { RouteLocationRaw } from 'vue-router'
import {
  computed,
  useSlots,
  warn,
  watch,
  type Component as ComponentType,
  type VNodeTypes,
} from 'vue'

export interface XButtonBaseProps {
  appearanceColor?:
    | 'theme-primary'
    | 'secondary-default'
    | 'secondary-start'
    | 'secondary-dark'
    | 'secondary-light'
    | 'secondary-white'
    | 'secondary-outline-light'
    | 'secondary-outline-dark'
    | 'destructive'
  appearanceShape?: 'standard' | 'pill' | 'round' | 'tab'
  appearanceSize?: 'standard' | 'medium' | 'small' | 'extra-small' | 'fill'
  appearanceDisplay?: 'block' | 'block-fill' | 'inline' | 'inline-fill'
  disabled?: boolean
  marketingEvent?: {
    name: string
    [key: string]: any
  }
  includeClickEvent?: boolean
  to?: RouteLocationRaw
  href?: string
  [key: string]: any
}

// Add more specific typing once https://github.com/vuejs/core/issues/8468 is resolved
// type XButtonRouterLinkProps = XButtonBaseProps & RouterLinkProps
// type XButtonAnchorProps = XButtonBaseProps & HTMLAnchorElement
// type XButtonButtonProps = XButtonBaseProps & HTMLButtonElement
// type XButtonProps =
//   | XButtonRouterLinkProps
//   | XButtonAnchorProps
//   | XButtonButtonProps

const props = withDefaults(defineProps<XButtonBaseProps>(), {
  appearanceColor: 'secondary-default',
  appearanceShape: 'standard',
  appearanceSize: 'standard',
  appearanceDisplay: 'block',
  marketingEvent: undefined,
  to: undefined,
  href: undefined,
})

const emit = defineEmits<{
  (e: 'click', event?: MouseEvent | null): void
}>()

const slots = useSlots()

const elementType = computed(() => {
  if (typeof props.to !== 'undefined') {
    return 'router-link'
  }
  if (typeof props.href !== 'undefined') {
    return 'a'
  }
  return 'button'
})

const typeStyleClassMap = {
  standard: 'ys-text-button1',
  medium: 'ys-text-button2',
  small: 'ys-text-button3',
  'extra-small': 'ys-text-button3',
  fill: 'ys-text-button1',
}

const buttonClasses = computed(() => {
  const classes = [
    `shape-${props.appearanceShape}`,
    `size-${props.appearanceSize}`,
    `display-${props.appearanceDisplay}`,
    props.appearanceColor,
    typeStyleClassMap[props.appearanceSize],
  ]

  // If button has both an icon and a text label, add the 'x-button-icon-and-text' class so we can control the icon
  // size and alignment. We want to avoid changing the icon size/alignment if the button *only* has an icon. The text
  // might either be a plain Text DOM node, or a label/span/div, so look at textContent to handle all scenarios.
  // Sometimes default is a function sometimes its a value.

  function isComponent(nodeType: VNodeTypes): nodeType is ComponentType {
    return (nodeType as ComponentType).name !== 'undefined'
  }

  const defaultSlot = isFunction(slots?.default)
    ? slots?.default()
    : slots?.default
  if (defaultSlot) {
    const nodes =
      defaultSlot.filter(
        (node) =>
          !(node?.children instanceof Object) ||
          (isComponent(node?.type) && node.type.name === 'x-icon')
      ) ?? []
    if (nodes.length === 2) {
      classes.push('x-button-icon-and-text')
    }
  }

  return ['x-button', [classes]]
})

const isLink = computed(() => {
  return !!(props.to || props.href)
})

const linkProps = computed(() => {
  if (isLink.value) {
    if (props.to) {
      return { to: props.to }
    }
    if (props.href) {
      return { href: props.href }
    }
  }
  return {}
})

const computedType = computed(() => {
  // Fallback behavior for <button> elements with an invalid type attribute is type="submit"
  if (!isLink.value && !props.type) {
    return 'button'
  }
  // If type is undefined, we only want a default value for button elements
  return props.type || (isLink.value ? undefined : 'button')
})

const computedDisabled = computed(() => {
  return isLink.value ? undefined : props.disabled
})

const propsWhenDisabled = computed(() => {
  return isLink.value && props.disabled
    ? {
        'aria-disabled': 'true',
        href: undefined,
        role: 'link',
        to: props.to ? '' : undefined,
      }
    : {}
})

watch(
  props,
  (newProps) => {
    if (newProps.to && newProps.href) {
      warn(
        `[onX Design System] Passed conflicting props to="${newProps.to}" and href="${newProps.href}" to an <x-button> component that is being rendered as an anchor element. This element will be treated as a Vue router-link, and perform client-side routing to the value passed to "to", but will appear with a different "href" value in the rendered DOM. Please remove one of these props.`
      )
    }
  },
  { immediate: true }
)

const click = (e: MouseEvent) => {
  if (props.disabled) {
    return
  }
  const arg = props.includeClickEvent ? e : null
  emit('click', arg)
  if (props.marketingEvent) {
    const { name, ...marketingEvent } = props.marketingEvent
    const emitEvent = new CustomEvent('global-marketing-event', {
      detail: { name, ...marketingEvent },
    })
    window.dispatchEvent(emitEvent)
  }
}
</script>

<style lang="scss" scoped>
@use 'sass:map';
@use '@/styles/system/_buttons.scss';
@use '@/styles/system/_type.scss';
@use '@/styles/system/_colors.scss';
@use '@/styles/system/_variables.scss';

@mixin apply-state($state) {
  @if $state == 'default' {
    @content;
  } @else if $state == 'disabled' {
    &:is(:disabled, [aria-disabled='true']) {
      @content;
    }
  } @else {
    &:#{$state} {
      @content;
    }
  }
}

@mixin apply-button-state-styles($map, $state) {
  @include apply-state($state) {
    background-color: map.get($map, $state, 'background');
    color: map.get($map, $state, 'color');
    border-color: map.get($map, $state, 'border-color');

    :deep(.x-icon svg) {
      fill: map.get($map, $state, 'color');
    }
  }
}

@mixin apply-button-styles($map) {
  @include apply-button-state-styles($map, 'default');
  @include apply-button-state-styles($map, 'hover');
  @include apply-button-state-styles($map, 'focus');
  @include apply-button-state-styles($map, 'active');
  @include apply-button-state-styles($map, 'disabled');

  &::before,
  &::after {
    background-color: map.get($map, 'buttonIconHighlight');
  }
}

.x-button {
  margin-bottom: 10px;
  justify-content: center;
  border: 2px solid transparent;
  text-decoration: none;

  &:is(:disabled, [aria-disabled='true']) {
    cursor: not-allowed;
  }

  &:not(:is(:disabled, [aria-disabled='true'])) {
    cursor: pointer;
  }

  &:focus {
    outline: none;
  }

  &.x-button-icon-and-text {
    :deep(.x-icon) {
      display: inline-flex;
      width: 16px;
      margin-right: 5px;
      margin-top: 2px;
    }
  }

  // color schemes
  &.theme-primary {
    @include apply-button-styles(buttons.$primary);
  }
  &.secondary-default {
    @include apply-button-styles(buttons.$secondaryDefault);
  }
  &.secondary-start {
    @include apply-button-styles(buttons.$secondaryStart);
  }
  &.secondary-dark {
    @include apply-button-styles(buttons.$secondaryDark);
  }
  &.secondary-light {
    @include apply-button-styles(buttons.$secondaryLight);
  }
  &.secondary-white {
    @include apply-button-styles(buttons.$secondaryWhite);
  }
  &.secondary-outline-light {
    @include apply-button-styles(buttons.$secondaryOutlineLight);
  }
  &.secondary-outline-dark {
    @include apply-button-styles(buttons.$secondaryOutlineDark);
  }
  &.destructive {
    @include apply-button-styles(buttons.$destructive);
  }

  // color scheme overrides
  &.secondary-light.size-small {
    @include apply-button-styles(buttons.$secondaryLightSmall);
  }
  &.secondary-dark.size-small {
    @include apply-button-styles(buttons.$secondaryDarkSmall);
  }
  &.secondary-outline-light.size-small {
    @include apply-button-styles(buttons.$secondaryOutlineLightSmall);
  }
  &.secondary-outline-dark.size-small {
    @include apply-button-styles(buttons.$secondaryOutlineDarkSmall);
  }

  // shapes
  &.shape-standard {
    border-radius: variables.$border-radius-lg;
  }
  &.shape-pill {
    border-radius: 100px;
  }
  &.shape-round {
    border-radius: 50%;
  }
  &.shape-tab {
    border-radius: 16px;
  }

  // sizes
  &.size-standard {
    @include type.button1Type;
    padding: 12px 18px;
    height: 48px;
    &.shape-round {
      width: 48px;
      padding: 15px;
    }
    &.shape-tab {
      height: 56px;
    }
  }
  &.size-medium {
    @include type.button2Type;
  }
  &.size-small {
    @include type.button3Type;
    padding: 8px 12px;
    height: 36px;
    &.shape-standard {
      border-radius: 10px;
    }
    &.shape-round {
      width: 34px;
      padding: 9px;
    }

    // icons
    &.x-button-icon-and-text {
      :deep(.x-icon) {
        width: 12px;
        margin-right: 3px;
      }
    }
  }
  &.size-extra-small {
    @include type.button3Type;
    padding: 6px 12px 8px 5px;
    height: 28px;
    &.shape-round {
      width: 28px;
      padding: 7px;
    }
  }
  &.size-fill {
    @include type.button1Type;
    min-width: 100%;
    min-height: 100%;
  }

  // display
  &.display-block {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
  }
  &.display-block-fill {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    width: 100%;
  }
  &.display-inline {
    display: inline-flex;
    flex-flow: row nowrap;
    align-items: center;
    margin: 0px 5px;
    &:first-of-type {
      margin-left: 0px;
    }
    &:last-of-type {
      margin-right: 0px;
    }
  }
  &.display-inline-fill {
    display: inline-flex;
    flex-flow: row nowrap;
    align-items: center;
    flex-grow: 2;
    margin: 0px 5px;
    &:first-of-type {
      margin-left: 0px;
    }
    &:last-of-type {
      margin-right: 0px;
    }
  }
}
</style>
